import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import {
  MapPin, Briefcase, CheckCircle, Star, ArrowRight, Home, Clock,
  Award, Phone, Mail, Globe, Shield, MessageSquare, Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useCurrencySettings } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AgentProfilePage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const { formatPrice: fmtPrice } = useCurrencySettings();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consultMessage, setConsultMessage] = useState('');
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchAgent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agent/profile/slug/${slug}`);
      setAgent(res.data);
    } catch {
      setAgent(null);
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => {
    fetchAgent();
  }, [slug, fetchAgent]);

  const handleConsultation = async () => {
    if (!user) {
      toast.error('Please login or register to request a consultation');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/consultations/request`, {
        professional_id: agent.id,
        service_type: agent.professional_type || 'estate_agent',
        message: consultMessage,
        country: agent.service_areas?.[0] || 'UK',
      });
      toast.success(`Consultation request sent to ${agent.name}!`);
      setShowConsultForm(false);
      setConsultMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex justify-center py-32">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-32 text-center">
          <h1 className="font-['Playfair_Display'] text-3xl text-[#0F172A] mb-4">Agent Not Found</h1>
          <p className="text-slate-500 mb-6">This profile doesn't exist or has been removed.</p>
          <Link to="/properties">
            <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E]">Browse Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  const listings = agent.listings || [];
  const initials = (agent.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2);
  const typeLabel = (agent.professional_type || 'agent').replace(/_/g, ' ');

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Banner */}
      <div className="bg-[#0F172A] py-12 sm:py-16" data-testid="agent-profile-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#E07A3A] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-2xl sm:text-3xl">{initials}</span>
            </div>
            <div className="text-center sm:text-left flex-1">
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-white font-semibold mb-1">{agent.name}</h1>
              <p className="text-[#E07A3A] text-xs font-semibold uppercase tracking-[3px] mb-3">{typeLabel}</p>
              <div className="flex flex-wrap justify-center sm:justify-start gap-4 text-sm text-white/60">
                {agent.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{agent.location}</span>}
                {agent.years_experience && <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" />{agent.years_experience}+ years experience</span>}
                <span className="flex items-center gap-1"><Home className="h-3.5 w-3.5" />{agent.total_listings || 0} listings</span>
                <span className="flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />{agent.completed_deals || 0} deals completed</span>
              </div>
              {agent.service_areas && agent.service_areas.length > 0 && (
                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                  {agent.service_areas.map(area => (
                    <span key={area} className="px-3 py-1 bg-white/10 text-white/70 text-[10px] uppercase tracking-wider font-semibold">{area === 'UK' ? 'United Kingdom' : area === 'IN' ? 'India' : area === 'CA' ? 'Canada' : area}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-shrink-0">
              {!showConsultForm ? (
                <Button
                  onClick={() => setShowConsultForm(true)}
                  className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-6 py-5 text-sm tracking-widest uppercase font-semibold"
                  data-testid="contact-agent-btn"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />Contact Technology Service Partner
                </Button>
              ) : (
                <div className="bg-white/10 p-4 w-72" data-testid="contact-form">
                  <textarea
                    value={consultMessage}
                    onChange={(e) => setConsultMessage(e.target.value)}
                    placeholder="Hi, I'm interested in your services..."
                    className="w-full bg-white text-[#0F172A] border-0 p-3 text-sm resize-none h-20 focus:outline-none"
                    data-testid="contact-message"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button onClick={handleConsultation} disabled={sending} className="flex-1 bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs" data-testid="send-contact-btn">
                      {sending ? 'Sending...' : 'Send'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowConsultForm(false)} className="text-white border-white/20 hover:bg-white/10 text-xs">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Bio & Qualifications */}
        {(agent.bio || agent.qualifications || agent.specializations) && (
          <div className="mb-10" data-testid="agent-about">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-4">About</h2>
            {agent.bio && <p className="text-slate-600 text-sm leading-relaxed mb-4">{agent.bio}</p>}
            <div className="flex flex-wrap gap-4">
              {agent.qualifications && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Award className="h-4 w-4 text-[#E07A3A]" />
                  <span><strong>Qualifications:</strong> {agent.qualifications}</span>
                </div>
              )}
              {agent.specializations && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Star className="h-4 w-4 text-[#E07A3A]" />
                  <span><strong>Specializations:</strong> {agent.specializations}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Property Listings */}
        <div data-testid="agent-listings">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-6">
            Properties by {agent.name}
            <span className="text-sm font-sans text-slate-400 ml-2">({agent.total_listings || 0})</span>
          </h2>

          {listings.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {listings.map((prop) => (
                <Link key={prop.id} to={`/property/${prop.id}`} className="group" data-testid={`listing-${prop.id}`}>
                  <div className="border border-slate-100 overflow-hidden hover:shadow-lg transition-shadow">
                    {prop.images && prop.images.length > 0 ? (
                      <div className="h-44 overflow-hidden">
                        <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    ) : (
                      <div className="h-44 bg-slate-100 flex items-center justify-center">
                        <Home className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="text-[#0F172A] font-semibold text-sm mb-1 truncate">{prop.title}</h3>
                      <p className="text-xs text-slate-400 flex items-center gap-1 mb-2"><MapPin className="h-3 w-3" />{prop.city}{prop.country ? `, ${prop.country}` : ''}</p>
                      {prop.price && (
                        <p className="text-[#E07A3A] font-bold text-base">{fmtPrice(prop.price, prop.currency?.toUpperCase())}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-400 uppercase tracking-wider">
                        {prop.bedrooms && <span>{prop.bedrooms} bed</span>}
                        {prop.bathrooms && <span>{prop.bathrooms} bath</span>}
                        {prop.property_type && <span>{prop.property_type}</span>}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="border border-slate-100 p-12 text-center">
              <Home className="h-10 w-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No properties listed yet.</p>
              <p className="text-sm text-slate-400 mt-1">Check back soon for new listings.</p>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-10 p-4 bg-slate-50 border border-slate-100" data-testid="liability-disclaimer">
          <p className="text-[10px] text-slate-400 leading-relaxed text-center">
            <Shield className="h-3 w-3 inline mr-1" />
            <strong>Disclaimer:</strong> Shopperden is a marketplace platform only. All property transactions, viewings, and professional services are conducted directly between the customer and the agent/professional. Shopperden does not verify agent qualifications, endorse any agent, or accept liability for services provided. Customers are advised to conduct their own due diligence and seek independent professional advice before entering into any agreement. By using this platform, you acknowledge that all decisions are made at your own discretion and risk.
          </p>
        </div>

        {/* CTA Banner */}
        <div className="mt-8 bg-[#0F172A] p-6 sm:p-8 text-center" data-testid="cta-banner">
          <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-white mb-2">Discover More on Shopperden</h3>
          <p className="text-white/50 text-xs sm:text-sm mb-6 max-w-lg mx-auto">Browse thousands of properties with 3D virtual tours, get free AI valuations, and connect with professionals across UK, India, and Canada.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/properties">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="browse-properties-btn">
                Browse All Properties <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="register-btn">
                Register Free
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfilePage;
